import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { play as sfx } from "@/utils/sounds";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const keyMap = { ArrowUp: "up", ArrowDown: "down", ArrowLeft: "left", ArrowRight: "right", w: "up", s: "down", a: "left", d: "right" };

const pickRandom = (list, exclude) => {
  const pool = list.filter(s => s.id !== exclude);
  return pool.length > 0 ? pool[Math.floor(Math.random() * pool.length)] : list[0];
};

const useRapidFire = (stratagems) => {
  const { user } = useAuth();
  const [phase, setPhase] = useState("countdown");
  const [count, setCount] = useState(3);
  const [current, setCurrent] = useState(null);
  const [nextUp, setNextUp] = useState(null);
  const [inputSeq, setInputSeq] = useState([]);
  const [completed, setCompleted] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [streak, setStreak] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [flash, setFlash] = useState(null);
  const [lockUntil, setLockUntil] = useState(null);

  const timerRef = useRef(null);
  const flashRef = useRef(null);
  const submittedRef = useRef(false);
  const stratagemsList = useRef([]);

  // Keep the list in sync with prop changes (initial load, admin edits)
  useEffect(() => {
    const filtered = stratagems.filter(s => s.code && s.code.length > 0);
    stratagemsList.current = filtered;
  }, [stratagems]);

  // Countdown
  useEffect(() => {
    if (phase !== "countdown") return;
    if (count <= 0) {
      const list = stratagemsList.current;
      const first = pickRandom(list, null);
      const next = pickRandom(list, first?.id);
      setCurrent(first);
      setNextUp(next);
      setStartTime(Date.now());
      setPhase("active");
      sfx("round_start");
      return;
    }
    sfx("countdown");
    const t = setTimeout(() => setCount(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, count]);

  // Elapsed timer
  useEffect(() => {
    if (phase !== "active") return;
    timerRef.current = setInterval(() => setElapsed(Date.now() - startTime), 100);
    return () => clearInterval(timerRef.current);
  }, [phase, startTime]);

  const advance = useCallback(() => {
    const list = stratagemsList.current;
    const next = pickRandom(list, nextUp?.id);
    setCurrent(nextUp);
    setNextUp(next);
    setInputSeq([]);
  }, [nextUp]);

  const endSession = useCallback(() => {
    if (phase !== "active" || submittedRef.current) return;
    submittedRef.current = true;
    clearInterval(timerRef.current);
    const totalMs = Date.now() - startTime;
    setElapsed(totalMs);
    setPhase("results");
    sfx("success");

    if (user && completed > 0) {
      axios.post(`${API}/practice/rapid-fire`, {
        completed, attempts, total_time_ms: totalMs, longest_streak: longestStreak, stratagems_hit: [],
      }).catch(() => toast.error("Could not save session — try again later."));
    } else if (!user) {
      toast.info("Sign in to save your Rapid Fire scores to the leaderboard.");
    }
  }, [phase, startTime, user, completed, attempts, longestStreak]);

  const handleInput = useCallback((direction) => {
    if (phase !== "active" || !current) return;
    if (lockUntil && Date.now() < lockUntil) return;

    const code = current.code;
    const idx = inputSeq.length;
    const expected = code[idx];

    if (direction !== expected) {
      sfx("fail");
      setAttempts(a => a + 1);
      setStreak(0);
      setFlash("fail");
      setLockUntil(Date.now() + 300);
      if (flashRef.current) clearTimeout(flashRef.current);
      flashRef.current = setTimeout(() => { setFlash(null); advance(); }, 250);
      setInputSeq([]);
      return;
    }

    const nextSeq = [...inputSeq, direction];
    if (nextSeq.length === code.length) {
      const newStreak = streak + 1;
      sfx(newStreak >= 3 ? "perfect" : "complete");
      setCompleted(c => c + 1);
      setAttempts(a => a + 1);
      setStreak(newStreak);
      setLongestStreak(ls => Math.max(ls, newStreak));
      setFlash("success");
      if (flashRef.current) clearTimeout(flashRef.current);
      flashRef.current = setTimeout(() => { setFlash(null); advance(); }, 300);
      setInputSeq([]);
    } else {
      sfx(`input_${direction}`);
      setInputSeq(nextSeq);
    }
  }, [phase, current, inputSeq, lockUntil, streak, advance]);

  // Keyboard
  useEffect(() => {
    const handler = (e) => {
      if (["INPUT", "SELECT", "TEXTAREA"].includes(e.target.tagName)) return;
      const dir = keyMap[e.key];
      if (dir) { e.preventDefault(); handleInput(dir); }
      if (e.key === "Escape" && phase === "active") endSession();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleInput, phase, endSession]);

  // Cleanup
  useEffect(() => () => {
    clearInterval(timerRef.current);
    if (flashRef.current) clearTimeout(flashRef.current);
  }, []);

  const restart = () => {
    submittedRef.current = false;
    setPhase("countdown");
    setCount(3);
    setCompleted(0);
    setAttempts(0);
    setStreak(0);
    setLongestStreak(0);
    setElapsed(0);
    setInputSeq([]);
    setFlash(null);
    setLockUntil(null);
  };

  const accuracy = attempts > 0 ? Math.round(completed / attempts * 100) : 0;
  const spm = elapsed > 0 ? (completed / (elapsed / 60000)).toFixed(1) : "0.0";

  return {
    phase, count, current, nextUp, inputSeq,
    completed, attempts, streak, longestStreak,
    elapsed, flash, lockUntil, accuracy, spm,
    handleInput, endSession, restart,
    isTouchDevice: "ontouchstart" in window || navigator.maxTouchPoints > 0,
  };
};

export default useRapidFire;
